import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { PosTask } from "../../interfaces/Task";
import { Media } from "../../interfaces/Media";
import Uploader from "../../elements/Uploader";
import CustomEditor from "../../elements/CustomEditor";
import Help from "../../elements/HelpContent";
import { handleApiError, safeJsonParse } from "../../../utils/api";
import { useNavigate } from "react-router-dom";
import AITools from "../../elements/AITools";
import CloneAs from "../../elements/CloneAs";
import { useTranslation } from "react-i18next";
import { Overlay } from "../../elements/Overlay";

const emptyPosTask: any = {
    _id: '',
    name: '',
    type: 'video',
    instructions: '',
    media: [],
    text: '',
    updated: new Date()
}

export interface EditPosTaskVideoProps {
    task_id: string;
    id: string;
    position: number;
    saveForm: CallableFunction;
    closeForm: CallableFunction;
    cloneAs: CallableFunction;
    task_language?: string;
}

export default function EditPosTaskVideo(props: EditPosTaskVideoProps) {
    const { t } = useTranslation();
    
    const navigate = useNavigate();
    const [posTask, setPosTask] = useState<PosTask>(emptyPosTask);
    const [name, setName] = useState('');
    const [instructions, setInstructions] = useState('');
    const [teacher_notes, setTeacherNotes] = useState('');
    const [text, setText] = useState('');
    const [media, setMedia] = useState<Media[]>([]);
    const [formattedText, setFormattedText] = useState('');

    const handleMediaUpload = (media: Media[]) => {
        const newMedia = media.map((m: Media) => {
            return m;
        });
        setMedia(newMedia);
    }

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value);
    }

    const handleInstructionsChange = (data: string) => {
        setInstructions(data);
    }

    const handleTeacherNotesChange = (data: string) => {
        setTeacherNotes(data);
    }

    const handleTextChange = (data: string) => {
        setText(data);
    }

    const handleClose = () => {
        props.closeForm();
    }

    const handleSave = () => {
        if (name) {
            posTask.name = name;
            posTask.instructions = instructions;
            posTask.media = media.map((m: Media) => { return m; });
            posTask.text = text;
            posTask.formatted_text = formattedText;
            posTask.teacher_notes = teacher_notes;
            props.saveForm(posTask);
        }
    }

    useEffect(() => {
        const requestOptionsPost = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('token')
            }
        };
        fetch(import.meta.env.VITE_API_URL + '/v1/task/' + props.task_id + '/postask/' + props.id, requestOptionsPost)
            .then(response => {
                if (handleApiError(response, navigate)) {
                    return null;
                }
                return safeJsonParse(response);
            })
            .then(data => {
                if (!data) return;
                const newMedia: Media[] = data.media.map((m: Media) => { return m; });
                setMedia(newMedia);
                setPosTask(data);
                setName(data.name);
                setFormattedText(data.formatted_text);
            });
    }, []);

    const handleCloneAs = (id: string, direction: string) => {
        props.cloneAs(id, direction);
    }

    const handleSaveFormattedText = (text: string) => {
        setFormattedText(text);
    }

    const doNothing = () => {
        
    }

    return(
        <Overlay
            title={ t("Edit Post-Task: Video") }
            onClose={ handleClose }
            actions={[
                {
                    label: t("Cancel"),
                    variant: 'secondary',
                    onClick: handleClose
                },
                {
                    label: t("Save"),
                    variant: 'primary',
                    onClick: handleSave
                }
            ]}
        >
            <div className="new-task-container video">
                <div className="inner">
                    <h2>
                        { t("Edit Post-Task: Video") }
                        <Help id={ '6527f89a1e5cf99991b5042c' } />
                    </h2>
                    <AITools />
                    <Form className="new-post-task-form">
                        <div className="fields-container">
                            <Form.Group className="field field-title">
                                <Form.Label>{ t("Title of the Post-Task") }</Form.Label>
                                <Form.Control type="text" name="name" required value={ name } onChange={ handleNameChange } />
                            </Form.Group>
                            <div className="field field-instructions">
                                <CustomEditor
                                    change={ handleInstructionsChange }
                                    changeFormatted={ doNothing }
                                    data={ posTask.instructions }
                                    formatted= { '' }
                                    id="instructions"
                                    label={ t("Aim and instructions") }
                                    npl={ false }
                                    task_id={ '' }
                                />
                            </div>
                            <div className="field field-teachernotes">
                                <CustomEditor
                                    change={ handleTeacherNotesChange }
                                    changeFormatted={ doNothing }
                                    data={ posTask.teacher_notes }
                                    formatted= { '' }
                                    id="teachernotes"
                                    label={ t("Teacher notes") }
                                    npl={ false }
                                    task_id={ '' }
                                />
                            </div>
                            <Uploader media={ media } save={ handleMediaUpload } />
                            <div className="field field-text">
                                <CustomEditor
                                    change={ handleTextChange }
                                    changeFormatted={ handleSaveFormattedText }
                                    data={ posTask.text }
                                    formatted={ posTask.formatted_text }
                                    id="text"
                                    label={ t("Text")}
                                    npl={ true }
                                    task_id={ props.task_id }
                                    task_language={ props.task_language }
                                />
                            </div>
                            <CloneAs id={ props.id } direction={ 'pos-to-pre' } cloneAs={ handleCloneAs } />
                        </div>
                    </Form>
                </div>
            </div>
        </Overlay>
    );
}